<template>
	<div class="content-wrapper">
		<!-- Content -->

		<div class="container flex-grow-1 container-p-y">
			<div class="spinner-border spinner-border-lg text-primary load-center" role="status" v-if="isLoading">
				<span class="visually-hidden">Loading...</span>
			</div>

			<div class="col-md-12 mb-5" v-if="company">
				<div class="card h-100">
					<div class="card-body pb-4">
						<div class="mb-5" v-if="company.billingMethod === 'credits'">
							<div class="d-flex align-items-center">
								<div class="avatar avatar-md">
									<div class="avatar-initial bg-label-primary rounded-3">
										<i class="ri-bank-card-line ri-24px"></i>
									</div>
								</div>
								<div class="ms-4">
									<h3 class="mb-0">{{ company.credits }}</h3>
									<p class="mb-0">{{ $t('transactions.availableCredits') }}</p>
								</div>
							</div>
						</div>
						<div class="" v-else>
							<div class="d-flex align-items-center">
								<div class="avatar avatar-md">
									<div class="avatar-initial bg-label-primary rounded-3">
										<i class="ri-bill-line ri-24px"></i>
									</div>
								</div>
								<div class="ms-4">
									<p class="mb-0"><strong>{{ $t('transactions.monthlyBilling') }}</strong> {{ $t('transactions.monthlyBillingDescription') }}</p>
								</div>
							</div>
						</div>
						<!-- ONLY FOR CREDITS -->
						<router-link to="/purchase-credits" v-if="company.billingMethod === 'credits'">
							<button class="btn btn-primary">
								{{ $t('transactions.buyCredits') }}
							</button>
						</router-link>
					</div>
				</div>
			</div>

			<div class="col-md-12" v-if="!isLoading && transactions.length > 0">
				<div class="card h-100">
					<div class="card-body" >
						<ul class="p-0 m-0">
							<li class="d-flex align-items-center pb-2" v-for="(transaction, index) in transactions" :key="transaction._id || index">
								<div class="me-4">
									<div class="avatar flex-shrink-0" v-if="transaction.type === 'increase' || transaction.type === 'refund'">
										<div class="avatar-initial bg-label-success rounded-3">
											<div>
												<i class="ri-add-circle-line"></i>
											</div>
										</div>
									</div>
									<div class="avatar flex-shrink-0" v-else-if="transaction.type === 'decrease'">
										<div class="avatar-initial bg-label-danger rounded-3">
											<div>
												<i class="ri-user-minus-line"></i>
											</div>
										</div>
									</div>
								</div>
								<div class="d-flex w-100 flex-wrap align-items-center justify-content-between gap-2">
									<div class="me-2" v-if="transaction.type === 'increase'">
										<h6 class="mb-1">{{ $t('transactions.creditPurchase') }}</h6>
									</div>
									<div class="me-2" v-else-if="transaction.type === 'refund'">
										<h6 class="mb-1">{{ $t('transactions.trainingCancellation', { name: transaction.formationName }) }}</h6>
										<p class="mb-0">{{ $t('transactions.traineeLabel', { name: transaction.user }) }}</p>
									</div>
									<div class="me-2" v-else-if="transaction.type === 'decrease'">
										<h6 class="mb-1">{{ $t('transactions.trainingLabel', { name: transaction.formationName }) }}</h6>
										<p class="mb-0">
											{{ $t('transactions.traineeLabel', { name: transaction.user }) }}
											<span v-if="transaction.createdBy">
												({{ $t('transactions.orderedBy', { date: formatDate(transaction.createdAt), createdBy: transaction.createdBy }) }})
											</span>
										</p>
									</div>
									<div class="user-progress d-flex align-items-center gap-2" v-if="transaction.type === 'decrease'">
										<h6 class="mb-0 text-danger">
											-{{ transaction.amount }}
											{{ transaction.billingMethod !== 'invoices' ? 'crédits' : '€' }}
										</h6>
									</div>

									<div class="user-progress d-flex align-items-center gap-2" v-if="transaction.type === 'increase' || transaction.type === 'refund'">
										<h6 class="mb-0 text-success">
											+{{ transaction.amount }}
											{{ transaction.billingMethod !== 'invoices' ? 'crédits' : '€' }}
										</h6>
									</div>
								</div>
							</li>
						</ul>
						<div ref="sentinelRef" class="py-2"></div>
						<div class="text-center py-3" v-if="isLoadingMore">
							<div class="spinner-border spinner-border-sm text-primary" role="status">
								<span class="visually-hidden">Loading...</span>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<!-- / Content -->
		<div class="content-backdrop fade"></div>
	</div>
</template>

<script>
import HTTP from "@/axios";
import moment from "moment";
import "moment/locale/fr";

moment.locale(localStorage.getItem('locale') || 'fr');

import { useInfiniteScroll } from "@/composables/useInfiniteScroll";

export default {
	name: "TransactionsPage",

	setup() {
		const infinite = useInfiniteScroll({
			limit: 150,
			fetchPage: async (page, limit) => {
				const response = await HTTP.get("/company/transactions", { params: { page, limit } });
				return {
					items: response.data.transactions,
					totalEntries: response.data.pagination?.totalEntries || 0,
					totalPages: response.data.pagination?.totalPages || 0
				};
			}
		});

		return {
			transactions: infinite.items,
			isLoading: infinite.isLoading,
			isLoadingMore: infinite.isLoadingMore,
			hasMore: infinite.hasMore,
			sentinelRef: infinite.sentinelRef
		};
	},

	data() {
		return {
			company: null
		};
	},

	methods: {
		async fetchCompany() {
			try {
				const response = await HTTP('/company');
				this.company = response.data.company;
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t('errors.generic');
				alert(errorMessage);
				console.error("Erreur API:", error);
			}
		},

		formatDate(date) {
			return moment(date).format("DD/MM/YYYY");
		},
	},

	async mounted() {
		this.fetchCompany();
	}
};
</script>

<style scoped>
/* Ajoutez des styles spécifiques à la page Dashboard */
.dashboard {
	padding: 1rem;
}

.card-title {
	font-size: 1.2rem;
}

.card-text {
	margin: 0;
}

.load-center {
	display: block;
	margin: 0 auto;
}
</style>
